// src/components/report-card/ReportCardGeneration.js
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert, Autocomplete, Box, Button, Card, CardContent, Checkbox, Chip,
  CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle,
  FormControl, FormControlLabel, InputLabel, MenuItem, Select, Switch,
  Grid, IconButton, Paper, Snackbar, Stack, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, TextField, Tooltip, Typography,
  useMediaQuery, useTheme
} from '@mui/material';
import { Print, Publish, Send, Visibility } from '@mui/icons-material';
import { useReactToPrint } from 'react-to-print';
import { reportCardAPI, academicAPI, studentAPI, schoolAPI, gradingAPI } from '../../services/api';
import ModulePageHeader from '../common/ModulePageHeader';
import ResponsiveRecordList, { EmptyRecordsState } from '../common/ResponsiveRecordList';
import TablePagination from '../common/TablePagination';
import ReportDocument from './print/ReportDocument';
import GenerateReportCardDialog from './forms/GenerateReportCardDialog';
import BulkEditReportCardsDialog from './forms/BulkEditReportCardsDialog';

const extractCollection = (r) => {
  const p = r?.data !== undefined ? r.data : r;
  if (Array.isArray(p)) return p;
  if (Array.isArray(p?.data)) return p.data;
  if (Array.isArray(p?.data?.data)) return p.data.data;
  return [];
};
const extractRecord = (r) => {
  const p = r?.data !== undefined ? r.data : r;
  return (p?.data && !Array.isArray(p.data)) ? p.data : p;
};
const readError = (e, fallback = 'Operation failed') => {
  if (e?.response?.status === 403) {
    return e?.response?.data?.message || 'You are not allowed to perform this action for the selected class/stream.';
  }
  return e?.response?.data?.message || e?.response?.data?.error || fallback;
};
const studentName = (s) => s?.full_name || `${s?.last_name || ''} ${s?.first_name || ''}`.trim() || s?.registration_no || `Student #${s?.id}`;
const className = (c) => c?.name || c?.class_name || 'Class';
const streamName = (s) => s?.display_name || s?.name || 'Stream';
const streamId = (s) => s?.stream_id ?? s?.id ?? null;

const sx = { width: '100%', minWidth: { sm: 240, lg: 280 } };

export default function ReportCardGeneration() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const printRef = useRef();
  const bulkPrintRef = useRef();
  const [bulkPrintData, setBulkPrintData] = useState([]);

  const [reportCards, setReportCards] = useState([]);
  const [pagination, setPagination] = useState({ current_page: 1, last_page: 1, per_page: 50, total: 0 });
  const [terms, setTerms] = useState([]);
  const [classes, setClasses] = useState([]);
  const [classStreams, setClassStreams] = useState([]);
  const [students, setStudents] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [selected, setSelected] = useState([]);
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(50);
  const [generateDialog, setGenerateDialog] = useState(false);
  const [viewDialog, setViewDialog] = useState(false);
  const [selectedReport, setSelectedReport] = useState(null);
  const [reportExtras, setReportExtras] = useState({ conduct: [], generalComment: null, school: null, gradeBoundaries: [] });
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [editForm, setEditForm] = useState({
    class_position: '',
    class_total_students: '',
    stream_position: '',
    stream_total_students: '',
    days_present: '',
    days_absent: '',
    is_promoted: false,
    promoted_to_class: '',
    report_card_template_id: ''
  });
  const [savingDetails, setSavingDetails] = useState(false);
  const [bulkEditDialog, setBulkEditDialog] = useState(false);
  const [bulkEditForm, setBulkEditForm] = useState({ days_present: '', days_absent: '', is_promoted: '', promoted_to_class: '' });
  const [savingBulk, setSavingBulk] = useState(false);

  const [form, setForm] = useState({ type: 'student', student_id: '', class_id: '', class_stream_id: '', term_id: '', academic_year: new Date().getFullYear(), report_card_template_id: '', days_present: '', days_absent: '', is_promoted: false, promoted_to_class: '' });
  const [filters, setFilters] = useState({ term_id: '', class_id: '', class_stream_id: '', academic_year: new Date().getFullYear() });

  const setFiltersAndReset = (updater) => {
    setFilters(updater);
    setPage(1);
  };

  const msg = (message, severity = 'success') => setSnackbar({ open: true, message, severity });

  const filterStreams = useMemo(() => filters.class_id ? classStreams.filter((s) => String(s.class_id) === String(filters.class_id)) : classStreams, [classStreams, filters.class_id]);
  const selectedFormStream = useMemo(() => classStreams.find((s) => String(s.id) === String(form.class_stream_id)) || null, [classStreams, form.class_stream_id]);
  const activeTemplate = useMemo(() => {
    if (!selectedReport) return null;
    return selectedReport.report_card_template
      || selectedReport.reportCardTemplate
      || templates.find((t) => String(t.id) === String(selectedReport.report_card_template_id))
      || null;
  }, [selectedReport, templates]);
  const loadData = useCallback(async () => {
    try {
      const [tr, csr, cr, sr, tmplRes] = await Promise.all([
        academicAPI.getTerms(),
        academicAPI.getClassStreamsForFilter(),
        academicAPI.getClasses(),
        studentAPI.getActive({ per_page: 1000 }),
        reportCardAPI.getReportCardTemplates()
      ]);
      const fetchedTerms = extractCollection(tr);
      setTerms(fetchedTerms);
      setClassStreams(extractCollection(csr));
      setClasses(extractCollection(cr));
      setStudents(extractCollection(sr));
      setTemplates(extractCollection(tmplRes));
      const current = fetchedTerms.find((t) => t.is_current || t.current);
      if (current) {
        const y = current.year || current.academic_year || new Date().getFullYear();
        setFilters((p) => ({ ...p, term_id: current.id, academic_year: y }));
        setForm((p) => ({ ...p, term_id: current.id, academic_year: y }));
      }
    } catch (e) { msg(readError(e, 'Failed to load data'), 'error'); }
  }, []);

  const loadReportCards = useCallback(async () => {
    setLoading(true);
    try {
      const filterStream = classStreams.find((s) => String(s.id) === String(filters.class_stream_id)) || null;
      const params = {
        academic_year: Number(filters.academic_year),
        page,
        per_page: perPage,
        sort_by: 'performance',
        ...(filters.term_id && { term_id: Number(filters.term_id) }),
        ...(filters.class_id && { class_id: Number(filters.class_id) }),
        ...(filterStream && { stream_id: Number(streamId(filterStream)), class_stream_id: Number(filterStream.id) })
      };
      const res = await reportCardAPI.getReportCards(params);
      const payload = res?.data !== undefined ? res.data : res;
      setReportCards(Array.isArray(payload?.data) ? payload.data : (Array.isArray(payload) ? payload : []));
      setPagination({
        current_page: payload?.current_page ?? 1,
        last_page: payload?.last_page ?? 1,
        per_page: payload?.per_page ?? perPage,
        total: payload?.total ?? 0,
      });
      setSelected([]);
    } catch (e) { msg(readError(e, 'Failed to load report cards'), 'error'); }
    finally { setLoading(false); }
  }, [classStreams, filters, page, perPage]);

  useEffect(() => { loadData(); }, [loadData]);
  useEffect(() => { loadReportCards(); }, [loadReportCards]);

  const handleGenerate = async () => {
    if (!form.term_id || !form.class_id || !form.class_stream_id) { msg('Select term, class and stream', 'error'); return; }
    if (form.type === 'student' && !form.student_id) { msg('Select a student', 'error'); return; }
    const sid = streamId(selectedFormStream);
    if (!sid) { msg('Invalid class stream', 'error'); return; }
    setProcessing(true);
    try {
      const payload = {
        class_id: Number(form.class_id),
        stream_id: Number(sid),
        class_stream_id: Number(form.class_stream_id),
        academic_year: Number(form.academic_year),
        term_id: Number(form.term_id),
        report_card_template_id: form.report_card_template_id ? Number(form.report_card_template_id) : null,
        ...(form.days_present !== '' && { days_present: Number(form.days_present) }),
        ...(form.days_absent !== '' && { days_absent: Number(form.days_absent) }),
        is_promoted: Boolean(form.is_promoted),
        ...(form.is_promoted && form.promoted_to_class && { promoted_to_class: form.promoted_to_class }),
      };
      console.log('[handleGenerate] Payload:', JSON.stringify(payload, null, 2), 'Type:', form.type);
      if (form.type === 'student') {
        await reportCardAPI.generateReportForStudent({ ...payload, student_id: Number(form.student_id) });
        msg('Report card generated for student');
      } else {
        await reportCardAPI.generateReportForClass(payload);
        msg('Report cards generated for class stream');
      }
      setGenerateDialog(false);
      await loadReportCards();
    } catch (e) {
      console.error('[handleGenerate] Full error:', e);
      console.error('[handleGenerate] Response status:', e?.response?.status);
      console.error('[handleGenerate] Response data:', JSON.stringify(e?.response?.data, null, 2));
      msg(readError(e, 'Failed to generate'), 'error');
    }
    finally { setProcessing(false); }
  };

  const handleView = async (id) => {
    try {
      setSelectedReport(null);
      setReportExtras({ conduct: [], generalComment: null, school: null });
      setViewDialog(true);
      const res = await reportCardAPI.getReportCard(id);
      const report = extractRecord(res);
      setSelectedReport(report);
      setEditForm({
        class_position: report?.class_position ?? '',
        class_total_students: report?.class_total_students ?? '',
        stream_position: report?.stream_position ?? '',
        stream_total_students: report?.stream_total_students ?? '',
        days_present: report?.days_present ?? '',
        days_absent: report?.days_absent ?? '',
        is_promoted: Boolean(report?.is_promoted),
        promoted_to_class: report?.promoted_to_class || '',
        report_card_template_id: report?.report_card_template_id ?? report?.report_card_template?.id ?? ''
      });

      // Fetch extras in parallel
      const studentId = report?.student_id || report?.student?.id;
      const termId = report?.term_id;
      const year = report?.academic_year;
      if (studentId && termId && year) {
        const params = { academic_year: year, term_id: termId };
        const [conductRes, commentRes, schoolRes, boundariesRes] = await Promise.allSettled([
          reportCardAPI.getStudentConductForStudent(studentId, params),
          reportCardAPI.getGeneralCommentForStudent(studentId, params),
          schoolAPI.get(),
          report?.class_id
            ? gradingAPI.getClassGradingScaleForClass(report.class_id, { academic_year: year })
            : Promise.resolve(null)
        ]);
        const conduct = conductRes.status === 'fulfilled' ? extractCollection(conductRes.value) : [];
        const gcPayload = commentRes.status === 'fulfilled' ? commentRes.value?.data : null;
        const generalComment = gcPayload?.data && !Array.isArray(gcPayload.data) ? gcPayload.data : gcPayload;
        const school = schoolRes.status === 'fulfilled' ? (schoolRes.value?.data?.data || schoolRes.value?.data || null) : null;
        const scalePayload = boundariesRes.status === 'fulfilled' ? boundariesRes.value?.data : null;
        const gradeBoundaries = scalePayload?.grading_scale?.grade_boundaries
          || scalePayload?.gradingScale?.grade_boundaries
          || scalePayload?.grading_scale?.gradeBoundaries
          || scalePayload?.gradingScale?.gradeBoundaries
          || [];
        setReportExtras({ conduct, generalComment, school, gradeBoundaries });
      }
    } catch (e) { msg(readError(e, 'Failed to load report card'), 'error'); }
  };

  const handleSaveDetails = async () => {
    if (!selectedReport) return;
    setSavingDetails(true);
    try {
      const payload = {
        class_position: editForm.class_position === '' ? null : Number(editForm.class_position),
        class_total_students: editForm.class_total_students === '' ? null : Number(editForm.class_total_students),
        stream_position: editForm.stream_position === '' ? null : Number(editForm.stream_position),
        stream_total_students: editForm.stream_total_students === '' ? null : Number(editForm.stream_total_students),
        days_present: editForm.days_present === '' ? null : Number(editForm.days_present),
        days_absent: editForm.days_absent === '' ? null : Number(editForm.days_absent),
        is_promoted: Boolean(editForm.is_promoted),
        promoted_to_class: editForm.is_promoted ? (editForm.promoted_to_class || null) : null,
        report_card_template_id: editForm.report_card_template_id === '' ? null : Number(editForm.report_card_template_id)
      };

      const res = await reportCardAPI.updateReportCard(selectedReport.id, payload);
      const updated = extractRecord(res);
      setSelectedReport((prev) => ({ ...prev, ...updated }));
      msg('Report card details updated');
      await loadReportCards();
    } catch (e) {
      msg(readError(e, 'Failed to update details'), 'error');
    } finally {
      setSavingDetails(false);
    }
  };

  const handlePublish = async (id) => {
    if (!window.confirm('Publish this report card?')) return;
    setProcessing(true);
    try {
      await reportCardAPI.publishReportCard(id);
      msg('Published successfully');
      await loadReportCards();
    } catch (e) { msg(readError(e, 'Failed to publish'), 'error'); }
    finally { setProcessing(false); }
  };

  const handleBulkUpdate = async () => {
    if (!selected.length) { msg('Select report cards to update', 'warning'); return; }
    setSavingBulk(true);
    try {
      const payload = { ids: selected };
      if (bulkEditForm.days_present !== '') payload.days_present = Number(bulkEditForm.days_present);
      if (bulkEditForm.days_absent !== '') payload.days_absent = Number(bulkEditForm.days_absent);
      if (bulkEditForm.is_promoted !== '') {
        payload.is_promoted = bulkEditForm.is_promoted === 'true';
        if (payload.is_promoted) payload.promoted_to_class = bulkEditForm.promoted_to_class || null;
      }
      const res = await reportCardAPI.bulkUpdateReportCards(payload);
      msg(res?.data?.message || 'Bulk update successful');
      setBulkEditDialog(false);
      setBulkEditForm({ days_present: '', days_absent: '', is_promoted: '', promoted_to_class: '' });
      await loadReportCards();
    } catch (e) { msg(readError(e, 'Bulk update failed'), 'error'); }
    finally { setSavingBulk(false); }
  };

  const handleBulkPublish = async () => {
    const unpublished = selected.filter((id) => {
      const card = reportCards.find((c) => c.id === id);
      return card && String(card.status || '').toLowerCase() !== 'published';
    });
    if (!unpublished.length) { msg('No unpublished cards selected', 'warning'); return; }
    if (!window.confirm(`Publish ${unpublished.length} report card(s)?`)) return;
    setProcessing(true);
    try {
      await Promise.all(unpublished.map((id) => reportCardAPI.publishReportCard(id)));
      msg(`${unpublished.length} report card(s) published`);
      await loadReportCards();
    } catch (e) { msg(readError(e, 'Bulk publish failed'), 'error'); }
    finally { setProcessing(false); }
  };

  const handlePrint = useReactToPrint({
    contentRef: printRef,
    pageStyle: '@page { margin: 0; } body { margin: 0; }',
    documentTitle: selectedReport ? `Report_${studentName(selectedReport.student)}` : 'Report Card',
  });

  const triggerBulkPrint = useReactToPrint({
    contentRef: bulkPrintRef,
    pageStyle: `
      @page { 
        margin: 0; 
        size: A4 portrait;
      } 
      body { 
        margin: 0; 
        padding: 0;
      }
      .rc-page {
        page-break-after: always;
      }
      .rc-page:last-child {
        page-break-after: auto;
      }
    `,
    documentTitle: 'Report_Cards_Bulk',
  });

  const handleBulkPrint = async () => {
    if (!selected.length) { msg('Select report cards to print', 'warning'); return; }
    setProcessing(true);
    try {
      const school = await schoolAPI.get().then((r) => r?.data?.data || r?.data || null).catch(() => null);
      const results = await Promise.all(
        selected.map(async (id) => {
          const report = extractRecord(await reportCardAPI.getReportCard(id));
          const studentId = report?.student_id || report?.student?.id;
          const params = { academic_year: report.academic_year, term_id: report.term_id };
          const [conductRes, commentRes, scaleRes] = await Promise.allSettled([
            reportCardAPI.getStudentConductForStudent(studentId, params),
            reportCardAPI.getGeneralCommentForStudent(studentId, params),
            report?.class_id ? gradingAPI.getClassGradingScaleForClass(report.class_id, { academic_year: report.academic_year }) : Promise.resolve(null)
          ]);
          const conduct = conductRes.status === 'fulfilled' ? extractCollection(conductRes.value) : [];
          const gcPayload = commentRes.status === 'fulfilled' ? commentRes.value?.data : null;
          const generalComment = gcPayload?.data && !Array.isArray(gcPayload.data) ? gcPayload.data : gcPayload;
          const scalePayload = scaleRes.status === 'fulfilled' ? scaleRes.value?.data : null;
          const gradeBoundaries = scalePayload?.grading_scale?.grade_boundaries
            || scalePayload?.gradingScale?.grade_boundaries
            || scalePayload?.grading_scale?.gradeBoundaries
            || scalePayload?.gradingScale?.gradeBoundaries
            || [];
          const template = templates.find((t) => String(t.id) === String(report.report_card_template_id)) || null;
          return { report, conduct, generalComment, school, template, gradeBoundaries };
        })
      );
      setBulkPrintData(results);
      // wait for render then print
      setTimeout(() => { 
        triggerBulkPrint(); 
        setTimeout(() => setProcessing(false), 1000);
      }, 800);
    } catch (e) { msg(readError(e, 'Failed to prepare bulk print'), 'error'); setProcessing(false); }
  };

  const toggleSelect = (id) => setSelected((p) => p.includes(id) ? p.filter((x) => x !== id) : [...p, id]);
  const toggleAll = () => setSelected(selected.length === reportCards.length ? [] : reportCards.map((c) => c.id));

  const pagedCards = reportCards;
  return (
    <Box>
      <ModulePageHeader
        title="Report Card Generation"
        subtitle="Generate, review, and publish report cards"
        actions={[
          <Button key="gen" variant="contained" onClick={() => setGenerateDialog(true)} fullWidth={isMobile}>
            Generate Report Cards
          </Button>
        ]}
      />

      <Card>
        <CardContent>
          <Grid container spacing={2} mb={2}>
            <Grid item xs={12} sm={6} md={3}>
              <TextField fullWidth size="small" type="number" label="Academic Year"
                value={filters.academic_year} onChange={(e) => setFiltersAndReset((p) => ({ ...p, academic_year: e.target.value }))}
                inputProps={{ min: 2000, max: 2100 }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Autocomplete fullWidth sx={sx} options={terms}
                value={terms.find((t) => String(t.id) === String(filters.term_id)) || null}
                onChange={(_, v) => setFiltersAndReset((p) => ({ ...p, term_id: v?.id || '' }))}
                getOptionLabel={(o) => `${o.name || 'Term'} ${o.year || ''}`.trim()}
                isOptionEqualToValue={(o, v) => o.id === v.id}
                renderInput={(p) => <TextField {...p} label="Term" size="small" />}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Autocomplete fullWidth sx={sx} options={classes}
                value={classes.find((c) => String(c.id) === String(filters.class_id)) || null}
                onChange={(_, v) => setFiltersAndReset((p) => ({ ...p, class_id: v?.id || '', class_stream_id: '' }))}
                getOptionLabel={className} isOptionEqualToValue={(o, v) => o.id === v.id}
                renderInput={(p) => <TextField {...p} label="Class" size="small" />}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Autocomplete fullWidth sx={sx} options={filterStreams}
                value={filterStreams.find((s) => String(s.id) === String(filters.class_stream_id)) || null}
                onChange={(_, v) => setFiltersAndReset((p) => ({ ...p, class_stream_id: v?.id || '' }))}
                getOptionLabel={streamName} isOptionEqualToValue={(o, v) => o.id === v.id}
                renderInput={(p) => <TextField {...p} label="Stream" size="small" />}
              />
            </Grid>
          </Grid>

          {selected.length > 0 && (
            <Box mb={2} display="flex" alignItems="center" gap={1}>
              <Typography variant="body2">{selected.length} selected</Typography>
              <Button size="small" variant="outlined" startIcon={<Publish />} onClick={handleBulkPublish} disabled={processing}>
                Bulk Publish
              </Button>
              <Button size="small" variant="outlined" startIcon={<Print />} onClick={handleBulkPrint} disabled={processing}>
                {processing ? 'Preparing...' : 'Bulk Print'}
              </Button>
              <Button size="small" variant="outlined" onClick={() => setBulkEditDialog(true)} disabled={processing}>
                Bulk Edit Attendance/Promotion
              </Button>
              <Button size="small" onClick={() => setSelected([])}>Clear</Button>
            </Box>
          )}

          {loading ? (
            <Box py={5} textAlign="center"><CircularProgress size={28} /></Box>
          ) : reportCards.length === 0 ? (
            <EmptyRecordsState message="No report cards found." />
          ) : isMobile ? (
            <>
              <ResponsiveRecordList items={pagedCards} renderItem={(report) => {
                const isPublished = String(report.status || '').toLowerCase() === 'published';
                return (
                  <Stack spacing={1}>
                    <Typography fontWeight={700}>{studentName(report.student)}</Typography>
                    <Typography variant="body2" color="text.secondary">{report.class?.name || '-'} · {report.term?.name || '-'} {report.academic_year || ''}</Typography>
                    <Box display="flex" justifyContent="space-between" alignItems="center">
                      <Chip label={report.status || 'Draft'} color={isPublished ? 'success' : 'default'} size="small" />
                      <Box>
                        <IconButton size="small" onClick={() => handleView(report.id)}><Visibility fontSize="small" /></IconButton>
                        {!isPublished && <IconButton size="small" color="primary" onClick={() => handlePublish(report.id)} disabled={processing}><Send fontSize="small" /></IconButton>}
                      </Box>
                    </Box>
                  </Stack>
                );
              }} />
              <TablePagination pagination={pagination} onPageChange={setPage} onPageSizeChange={(s) => { setPerPage(s); setPage(1); }} isMobile />
            </>
          ) : (
            <TableContainer component={Paper} elevation={0} sx={{ overflowX: 'auto' }}>
              <Table sx={{ minWidth: 860 }}>
                <TableHead>
                  <TableRow>
                    <TableCell padding="checkbox">
                      <Checkbox checked={pagedCards.length > 0 && selected.length === pagedCards.length} indeterminate={selected.length > 0 && selected.length < pagedCards.length} onChange={toggleAll} size="small" />
                    </TableCell>
                    <TableCell>Student</TableCell>
                    <TableCell>Class</TableCell>
                    <TableCell>Term</TableCell>
                    <TableCell>Overall %</TableCell>
                    <TableCell>Grade</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Generated</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {pagedCards.map((report) => {
                    const isPublished = String(report.status || '').toLowerCase() === 'published';
                    return (
                      <TableRow key={report.id} selected={selected.includes(report.id)}>
                        <TableCell padding="checkbox">
                          <Checkbox checked={selected.includes(report.id)} onChange={() => toggleSelect(report.id)} size="small" />
                        </TableCell>
                        <TableCell>{studentName(report.student)}</TableCell>
                        <TableCell>{report.class?.name || '-'}{report.stream?.name ? ` - ${report.stream.name}` : ''}</TableCell>
                        <TableCell>{report.term?.name || '-'} {report.academic_year || ''}</TableCell>
                        <TableCell>{report.overall_percentage ?? '-'}</TableCell>
                        <TableCell>{report.overall_grade || '-'}</TableCell>
                        <TableCell><Chip label={report.status || 'Draft'} color={isPublished ? 'success' : 'default'} size="small" /></TableCell>
                        <TableCell>{report.created_at ? new Date(report.created_at).toLocaleDateString() : '-'}</TableCell>
                        <TableCell>
                          <Tooltip title="View"><IconButton size="small" onClick={() => handleView(report.id)}><Visibility fontSize="small" /></IconButton></Tooltip>
                          {!isPublished && <Tooltip title="Publish"><IconButton size="small" color="primary" onClick={() => handlePublish(report.id)} disabled={processing}><Send fontSize="small" /></IconButton></Tooltip>}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
              <TablePagination pagination={pagination} onPageChange={setPage} onPageSizeChange={(s) => { setPerPage(s); setPage(1); }} isMobile={isMobile} />
            </TableContainer>
          )}
        </CardContent>
      </Card>

      {/* Generate Dialog */}
      <GenerateReportCardDialog
        open={generateDialog}
        onClose={() => setGenerateDialog(false)}
        onSubmit={handleGenerate}
        saving={processing}
        isMobile={isMobile}
        form={form}
        setForm={setForm}
        terms={terms}
        classes={classes}
        classStreams={classStreams}
        students={students}
        templates={templates}
        allClasses={classes}
      />

      {/* View Dialog */}
      <Dialog open={viewDialog} onClose={() => setViewDialog(false)} maxWidth="md" fullWidth fullScreen={isMobile}>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          Report Card
          {selectedReport && (
            <Chip
              label={selectedReport.status || 'Draft'}
              color={String(selectedReport.status || '').toLowerCase() === 'published' ? 'success' : 'default'}
              size="small"
            />
          )}
        </DialogTitle>
        <DialogContent sx={{ p: 1, bgcolor: '#e0e0e0' }}>
          {!selectedReport ? (
            <Box py={6} textAlign="center"><CircularProgress size={28} /></Box>
          ) : (
            <>
              <ReportDocument
                ref={printRef}
                report={selectedReport}
                school={reportExtras.school}
                conduct={reportExtras.conduct}
                generalComment={reportExtras.generalComment}
                gradeBoundaries={reportExtras.gradeBoundaries}
                template={activeTemplate}
              />
              <Box mt={2} p={2} bgcolor="#fff" borderRadius={1}>
                <Typography variant="subtitle1" gutterBottom>Report Card Details</Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6} md={4}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Class Position"
                      type="number"
                      value={editForm.class_position}
                      onChange={(e) => setEditForm((p) => ({ ...p, class_position: e.target.value }))}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Class Total Students"
                      type="number"
                      value={editForm.class_total_students}
                      onChange={(e) => setEditForm((p) => ({ ...p, class_total_students: e.target.value }))}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Stream Position"
                      type="number"
                      value={editForm.stream_position}
                      onChange={(e) => setEditForm((p) => ({ ...p, stream_position: e.target.value }))}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Stream Total Students"
                      type="number"
                      value={editForm.stream_total_students}
                      onChange={(e) => setEditForm((p) => ({ ...p, stream_total_students: e.target.value }))}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Template</InputLabel>
                      <Select
                        label="Template"
                        value={editForm.report_card_template_id}
                        onChange={(e) => setEditForm((p) => ({ ...p, report_card_template_id: e.target.value }))}
                      >
                        <MenuItem value="">Default</MenuItem>
                        {templates.map((t) => (
                          <MenuItem key={t.id} value={t.id}>{t.template_name}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Days Present"
                      type="number"
                      value={editForm.days_present}
                      onChange={(e) => setEditForm((p) => ({ ...p, days_present: e.target.value }))}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Days Absent"
                      type="number"
                      value={editForm.days_absent}
                      onChange={(e) => setEditForm((p) => ({ ...p, days_absent: e.target.value }))}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={4} display="flex" alignItems="center">
                    <FormControlLabel
                      control={
                        <Switch
                          checked={editForm.is_promoted}
                          onChange={(e) => setEditForm((p) => ({ ...p, is_promoted: e.target.checked }))}
                        />
                      }
                      label="Promoted"
                    />
                  </Grid>
                  {editForm.is_promoted && (
                    <Grid item xs={12} sm={6} md={6}>
                      <TextField
                        fullWidth
                        size="small"
                        label="Promoted To Class"
                        value={editForm.promoted_to_class}
                        onChange={(e) => setEditForm((p) => ({ ...p, promoted_to_class: e.target.value }))}
                      />
                    </Grid>
                  )}
                </Grid>
                <Box mt={2}>
                  <Button variant="contained" onClick={handleSaveDetails} disabled={savingDetails}>
                    {savingDetails ? 'Saving...' : 'Save Details'}
                  </Button>
                </Box>
              </Box>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setViewDialog(false)}>Close</Button>
          {selectedReport && String(selectedReport.status || '').toLowerCase() !== 'published' && (
            <Button variant="outlined" color="primary" startIcon={<Send />}
              onClick={() => { handlePublish(selectedReport.id); setViewDialog(false); }} disabled={processing}>
              Publish
            </Button>
          )}
          <Button variant="contained" startIcon={<Print />} onClick={handlePrint} disabled={!selectedReport}>Print</Button>
        </DialogActions>
      </Dialog>

      {/* Bulk Edit Attendance/Promotion Dialog */}
      <BulkEditReportCardsDialog
        open={bulkEditDialog}
        onClose={() => setBulkEditDialog(false)}
        onSubmit={handleBulkUpdate}
        saving={savingBulk}
        isMobile={isMobile}
        selectedCount={selected.length}
        form={bulkEditForm}
        setForm={setBulkEditForm}
        allClasses={classes}
      />

      <Snackbar open={snackbar.open} autoHideDuration={5000} onClose={() => setSnackbar((p) => ({ ...p, open: false }))}>
        <Alert severity={snackbar.severity} onClose={() => setSnackbar((p) => ({ ...p, open: false }))} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* Hidden bulk print container */}
      <div style={{ display: 'none' }}>
        <div ref={bulkPrintRef}>
          {bulkPrintData.map(({ report, conduct, generalComment, school, template, gradeBoundaries }, i) => (
            <div key={report.id} style={{ pageBreakAfter: i < bulkPrintData.length - 1 ? 'always' : 'auto' }}>
              <ReportDocument
                report={report}
                school={school}
                conduct={conduct}
                generalComment={generalComment}
                gradeBoundaries={gradeBoundaries || []}
                template={template}
              />
            </div>
          ))}
        </div>
      </div>
    </Box>
  );
}